'use client';

import { c as _c } from "react/compiler-runtime";
import './nav-stack.scss';
import { useRouter } from 'next/navigation';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { AuthStatusEnum, useAuth } from '@sola/auth-sdk/store.tsx';
import { getPublicConfig } from '@sola/config/lib/getters.ts';
import { NavigationProps } from '@sola/schema/lib/schema.ts';
import { trackClick } from '@sola/tracking/components/datalayer.tsx';
import { Dialpad } from '../../atoms/dialpad/dialpad.tsx';
import { LinkList } from '../../atoms/link-list/link-list.tsx';
import { NavCard } from '../nav-card/index.ts';
export type NavLevel = {
  items: NavigationProps[];
  parent?: NavigationProps;
};
type NavStackProps = {
  stack: NavLevel[];
  setStack: React.Dispatch<React.SetStateAction<NavLevel[]>>;
  showMyAccountSection?: boolean;
  className?: string;
};

// TODO: this should be its own component
const MobileAccountSection: FC = () => {
  const $ = _c(23);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getPublicConfig();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    NEXT_PUBLIC_BASE_URL_MYACCOUNT
  } = t0;
  const {
    actions: authActions
  } = useAuth("actions");
  const {
    currentStatus: currentAuthStatus
  } = useAuth("currentStatus");
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["priority"];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    t
  } = useTranslation(t1);
  let t2;
  if ($[2] !== authActions || $[3] !== t) {
    t2 = async () => {
      trackClick(t("nav.logOut"));
      authActions.logOut();
    };
    $[2] = authActions;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const logOut = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ArkIcon iconName="user-01" className="NavStack__icon NavStack__icon--filled" />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = NEXT_PUBLIC_BASE_URL_MYACCOUNT && <LinkStyle size="xl" noUnderline={true} noPadding={true} purpose="neutral"><Link href={NEXT_PUBLIC_BASE_URL_MYACCOUNT} className="label label-lg label-potent">{t("header.myAccount")}</Link></LinkStyle>;
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <span className="flex py-snug-xl gap-comfy-xs items-center">{t3}{t4}</span>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== currentAuthStatus || $[11] !== logOut || $[12] !== t) {
    t6 = currentAuthStatus === AuthStatusEnum.Authenticated && <span className="flex py-snug-xl gap-comfy-xs items-center"><ArkIcon iconName="log-out-01" className="NavStack__icon" /><LinkStyle size="xl" noUnderline={true} noPadding={true} purpose="negative"><button type="button" onClick={logOut}>{t("nav.logOut")}</button></LinkStyle></span>;
    $[10] = currentAuthStatus;
    $[11] = logOut;
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ArkIcon iconName="message-chat-circle" className="NavStack__icon" />;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t) {
    t8 = t("header.contactUs");
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <span className="flex py-snug-xl gap-comfy-xs items-center">{t7}<LinkStyle size="xl" noUnderline={true} noPadding={true} purpose="neutral"><Link href="/about-us/contact" className="label label-lg label-potent">{t8}</Link></LinkStyle></span>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t5 || $[20] !== t6 || $[21] !== t9) {
    t10 = <div className="NavStack__mobile-accounts-section p-comfy-sm sm:p-comfy-xl"><div className="flex flex-col">{t5}{t6}{t9}</div></div>;
    $[19] = t5;
    $[20] = t6;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
};
export const NavStack: React.FC<NavStackProps> = t0 => {
  const $ = _c(37);
  const {
    stack,
    setStack,
    showMyAccountSection,
    className
  } = t0;
  const router = useRouter();
  const stackIndex = stack.length - 1;
  const activeLevel = stack[stackIndex];
  const activeNavItems = activeLevel.items;
  const {
    parent
  } = activeLevel;
  const isRootNav = stack.length === 1;
  let t1;
  if ($[0] !== activeNavItems || $[1] !== isRootNav) {
    t1 = !isRootNav && activeNavItems.every(_temp) && activeNavItems.every(_temp2);
    $[0] = activeNavItems;
    $[1] = isRootNav;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const useSmallLinkList = t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[3] !== activeNavItems || $[4] !== className || $[5] !== isRootNav || $[6] !== parent?.cta || $[7] !== parent?.link || $[8] !== parent?.title || $[9] !== router || $[10] !== setStack || $[11] !== useSmallLinkList) {
    const hasIcons = activeNavItems.some(_temp3);
    if ($[17] !== activeNavItems) {
      t5 = activeNavItems.map(_temp4).join();
      $[17] = activeNavItems;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    if ($[19] !== className) {
      t6 = cn("NavStack py-comfy-sm flex flex-col gap-comfy-sm", className);
      $[19] = className;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    t2 = "flex flex-col gap-comfy-sm";
    if ($[21] !== isRootNav || $[22] !== parent?.link || $[23] !== parent?.title) {
      t3 = !isRootNav && <div className="p-comfy-sm sm:px-comfy-xl flex flex-col gap-snug-xl"><div className="NavStack__top-link-container pb-snug-md"><LinkStyle size="xl" noPadding={true} noUnderline={true} iconAfter="arrow-right"><Link href={parent?.link || "/"}>{parent?.title}</Link></LinkStyle></div></div>;
      $[21] = isRootNav;
      $[22] = parent?.link;
      $[23] = parent?.title;
      $[24] = t3;
    } else {
      t3 = $[24];
    }
    t4 = useSmallLinkList ? <div className="px-comfy-sm sm:px-comfy-xl"><LinkList item={{
        items: activeNavItems
      }} /></div> : <><ul className="gap-snug-xl flex flex-col m-none p-none">{activeNavItems.map(item => {
          const hasChildItems = item?.items?.length;
          const {
            displayVariant
          } = item;
          return <li className={cn("flex flex-col m-none p-none", {
            "NavStack__items--border mt-comfy-xs": displayVariant
          })} key={item.title}><div className={cn("flex justify-between", {
              "flex-col items-start": displayVariant,
              "items-center": !displayVariant
            })}><button type="button" onClick={() => {
                if (!hasChildItems && item.link) {
                  trackClick(item.title || item.description || "Unknown", item.link, "NavStack__link");
                  if (item?.target === "_blank") {
                    window.open(item?.link, item.target, "noopener,noreferrer");
                  } else {
                    router.push(item?.link);
                  }
                  return;
                }
                if (item.items) {
                  setStack(prev => [...prev, {
                    items: item.items as NavigationProps[],
                    parent: item
                  }]);
                }
              }} className={cn("NavStack__link flex justify-between items-center w-full px-comfy-sm sm:px-comfy-xl py-snug-xl", {
                "gap-snug-md": item.description,
                "py-comfy-sm": displayVariant
              })}><div className="flex gap-comfy-xs">{item?.icon && <ArkIcon iconName={item.icon} className="NavStack__icon" />}<div className={cn("flex flex-col", {
                    "NavStack__item-title--padding": hasIcons && !item?.icon,
                    "NavStack__item-title--padding--display-variant": displayVariant,
                    "gap-snug-md": item.description,
                    "pr-roomy-sm": !hasChildItems
                  })}><span className="flex gap-snug-xl items-center"><p className={cn("NavStack__text label label-lg label-neutral label-potent flex gap-snug-xl items-center", {
                        "label-xl label-potent": isRootNav
                      })}>{item.title}</p>{item.badge?.text && <span className="NavStack__badge"><Badge label={item.badge?.text} color={item.badge?.color} size="sm" /></span>}</span><p className="NavStack__description label-md label-moderate">{item.description}</p></div></div>{Boolean(hasChildItems) && !displayVariant && <div className={cn("NavStack__next-button ml-snug-md p-comfy-xs")}><ArkIcon iconName="chevron-right" /></div>}{isRootNav && !hasChildItems && !displayVariant && <div className={cn("NavStack__next-button ml-snug-md p-comfy-xs")}><ArkIcon iconName="arrow-right" /></div>}</button>{Boolean(hasChildItems) && displayVariant === "grid" && <div className="pt-snug-xl px-comfy-sm sm:px-comfy-xl"><Dialpad item={item} /></div>}{Boolean(hasChildItems) && displayVariant === "social" && <div className="pt-snug-xl px-comfy-sm sm:px-comfy-xl"><LinkList item={item} /></div>}</div></li>;
        })}</ul>{parent?.cta && <div className="NavCard__container pt-snug-xl px-comfy-sm sm:px-comfy-xl mr-auto"><NavCard {...parent?.cta} /></div>}</>;
    $[3] = activeNavItems;
    $[4] = className;
    $[5] = isRootNav;
    $[6] = parent?.cta;
    $[7] = parent?.link;
    $[8] = parent?.title;
    $[9] = router;
    $[10] = setStack;
    $[11] = useSmallLinkList;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
  }
  let t7;
  if ($[25] !== t2 || $[26] !== t3 || $[27] !== t4) {
    t7 = <div className={t2}>{t3}{t4}</div>;
    $[25] = t2;
    $[26] = t3;
    $[27] = t4;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== isRootNav || $[30] !== showMyAccountSection) {
    t8 = isRootNav && showMyAccountSection && <MobileAccountSection />;
    $[29] = isRootNav;
    $[30] = showMyAccountSection;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== t5 || $[33] !== t6 || $[34] !== t7 || $[35] !== t8) {
    t9 = <div key={t5} className={t6}>{t7}{t8}</div>;
    $[32] = t5;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  return t9;
};
function _temp(i_0) {
  return !i_0.items?.length;
}
function _temp2(i) {
  return !i.description;
}
function _temp3(i_1) {
  return Boolean(i_1.icon);
}
function _temp4(i_2) {
  return i_2.title;
}